import { isDotVotingStageOptions, isGroupingStageOptions, isIdea, isIdeaGroup, isIdeationStageOptions } from '@proj-common/type-helpers'
import { DeepPartial, DotVotingStageOptions, IEventProgressState, IEventProgressStateChange, IIdeateTopListItem, Idea, IdeationStageOptions, Stage, StageGroup, StageOptions, StageResult, StageStepAction, Step, SummedIdea, Template, uuidv4 } from '@proj-common/types'
import { isServer } from '@proj-common/utils'
import { useEffect, useRef, useState } from 'react'
import { Dictionary, isEqual } from 'lodash'
import { v4 as getUuidv4 } from 'uuid'

export const isDeveloperUser = (userData) => {
    if (!isServer) {
        let urlParams = new URLSearchParams(window.location.search)
        let demoUrlParam = urlParams.get("demo")
        return (
            demoUrlParam == "1" ||
            (userData && (userData.email == "demo@ideate.live" || userData.email == "szakacsszilard@gmail.com" || userData.email == "zeroxx1986@gmail.com" || userData.email == "hello@ideate.live"))
        )
    }
}

export const estimateDuration = (type, numberOfParticipants) => {
    let avgIdeasPerParticipant = 5
    let duration = 5
    let minsToReadAnIdea = 0.1

    if (type === 'ideation') {
        // default estimate
    }
    if (type === 'dotvoting') {
        if (numberOfParticipants > 5) {
            duration = Math.round(Math.floor(avgIdeasPerParticipant * numberOfParticipants * minsToReadAnIdea) / 5) * 5
        }
    }
    if (type === 'grouping') {
        // more people means more discussion
        if (numberOfParticipants > 10) {
            duration = 10
        }
    }

    return duration
}

//#region Event Progress

export const getEventProgressTitle = (stageGroupName, stepName, stageName) => {
    let title = '';

    if (stageName && stepName && stageGroupName) {
        title = `${stepName}: ${stageGroupName}`;
    } else if (stageName && stepName) {
        title = `${stageName}: ${stepName}`;
    } else if (stageName && stageGroupName) {
        title = `${stageName}: ${stageGroupName}`;
    } else if (stageName) {
        title = stageName;
    }

    return title;
};


export const getEventProgressDescription = (stepDescription: string, stageDescription: string) => {
    const description = `${stepDescription ? stepDescription : stageDescription}`
    return description
}

// TODO: Lobby support
// TODO: Completed/Archived support
/* document function
 * @param currentstage: Expects server side/redux currentstate
 * @param currentstep: Expects server side/redux currentstate
 */
export const getEventProgressState = (
    template: Template,
    currentstage: Stage,
    currentstep: Step,
    started: boolean,
    closed: boolean,
    halted: boolean,
    calculateChanges?: boolean
): IEventProgressState => {
    var eventProgressState: IEventProgressState = {
        title: null,
        description: null,
        instructions: null,
        backGroup: null,
        backStage: null,
        backStep: null,
        currentGroup: null,
        currentStage: null,
        currentStep: null,
        isHalted: false,
        isStarted: false,
        isClosed: false,
        nextGroup: null,
        nextStage: null,
        nextStep: null,
        nextChange: null,
        backChange: null,
    }

    // TODO: test
    const isCurrentLobby = currentstage == null && !closed && !started
    if (isCurrentLobby) {
        // Lobby: no back-, and current- properties, next- properties are the the first stage in the template
        eventProgressState.nextStage = template.stages[0]
        eventProgressState.nextStep = (eventProgressState.nextStage?.options as IdeationStageOptions)?.steps[0]
        eventProgressState.nextGroup = getGroupById(template, eventProgressState.nextStage.group)
        eventProgressState.nextChange = {
            isChangeNext: true,
            isChangeBack: false,
            isGroupChange: template.stages[0]?.group != null,
            isStageChange: true,
            isStepChange: isIdeationStageOptions(eventProgressState.nextStage?.options) && eventProgressState.nextStage?.options?.steps.length > 0,
            isHaltChange: false,
            isCloseChange: false,
        }
        return eventProgressState
    }

    // current
    eventProgressState.currentStage = currentstage
    eventProgressState.currentStep = currentstep
    eventProgressState.currentGroup = getGroupById(template, currentstage?.group)

    eventProgressState.isStarted = started
    eventProgressState.isClosed = closed
    eventProgressState.isHalted = halted

    // next
    eventProgressState.nextStage = getNextStage(template, currentstage, currentstep)
    eventProgressState.nextStep = getNextStep(template, currentstage, currentstep)
    eventProgressState.nextGroup = getGroupById(template, eventProgressState.nextStage?.group)

    // previous
    eventProgressState.backStage = getBackStage(template, currentstage, currentstep)
    eventProgressState.backStep = getBackStep(template, currentstage, currentstep)
    eventProgressState.backGroup = getGroupById(template, eventProgressState.backStage?.group)

    // title & description
    eventProgressState.title = closed ? null : getEventProgressTitle(
        eventProgressState.currentGroup?.name,
        eventProgressState.currentStep?.name,
        eventProgressState.currentStage?.name
    )
    eventProgressState.description = getEventProgressDescription(eventProgressState.currentStep?.description, eventProgressState.currentStage?.description)
    eventProgressState.instructions = eventProgressState.currentStage?.instructions

    if (calculateChanges) {
        // TODO: test
        let nextEventProgressState = getEventProgressState(template, eventProgressState.nextStage, eventProgressState.nextStep, started, closed, halted)
        //console.log("CALLED FROM getEventProgressState")
        eventProgressState.nextChange = getEventProgressStateChange(eventProgressState, nextEventProgressState, started, closed)

        // TODO: test
        let backEventProgressState = getEventProgressState(template, eventProgressState.backStage, eventProgressState.backStep, started, closed, halted)
        //console.log("CALLED FROM getEventProgressState")
        eventProgressState.backChange = getEventProgressStateChange(eventProgressState, backEventProgressState, started, closed)
    }

    return eventProgressState
}

//#endregion

//#region Event progress direction helpers

export const getEventProgressStateChange = (
    fromProgressState: IEventProgressState,
    toProgressState: IEventProgressState,
    started: boolean,
    closed: boolean
): IEventProgressStateChange => {
    let eventProgressStateChange: IEventProgressStateChange = {
        isChangeNext: false,
        isChangeBack: false,
        isGroupChange: false, //TODO: seems to be buggy, confirm with tests
        isStageChange: false,
        isStepChange: false,
        isHaltChange: false,
        isCloseChange: false,
    }

    eventProgressStateChange.isChangeBack = isChangeBack(fromProgressState, toProgressState)
    eventProgressStateChange.isChangeNext = isChangeNext(fromProgressState, toProgressState)
    eventProgressStateChange.isGroupChange = isGroupChange(fromProgressState, toProgressState)
    eventProgressStateChange.isStageChange = isStageChange(fromProgressState, toProgressState)
    eventProgressStateChange.isStepChange = isStepChange(fromProgressState, toProgressState)
    eventProgressStateChange.isHaltChange = isHaltChange(fromProgressState, toProgressState, closed)
    eventProgressStateChange.isCloseChange = isCloseChange(fromProgressState, toProgressState, closed)

    // const doDiff = (a, b) => {
    //     if (a == null && b == null) return "both null"
    //     if (a == null) return "a null"
    //     if (b == null) return "b null"
    //     let keys = Object.keys(a)
    //     let diff = {}
    //     keys.forEach(key => {
    //         if (isEqual(a[key], b[key])) return
    //         diff[key] = [a[key], b[key]]
    //     })
    //     return diff
    // }
    // console.table({ started, closed })
    // console.table(doDiff(fromProgressState, toProgressState))
    // console.table(eventProgressStateChange)
    return eventProgressStateChange
}

const isChangeBack = (fromProgressState: IEventProgressState, toProgressState: IEventProgressState) => {
    if (!fromProgressState || !toProgressState || fromProgressState.backStage == null || toProgressState.currentStage == null) return false
    let isChangeBack = false
    let isBackStep = false
    let isBackStage = false

    if (isIdeationStageOptions(fromProgressState.backStage?.options)) {
        isBackStep = fromProgressState.backStep?.id == toProgressState.currentStep?.id
        isBackStage = fromProgressState.backStage?.id == toProgressState.currentStage?.id
        isChangeBack = isBackStep && isBackStage
    } else {
        isBackStage = fromProgressState.backStage?.id == toProgressState.currentStage?.id
        isChangeBack = isBackStage
    }

    return isChangeBack
}

const isChangeNext = (fromProgressState: IEventProgressState, toProgressState: IEventProgressState) => {
    if (!fromProgressState || !toProgressState) return false

    // if start of the event
    if (fromProgressState.isStarted == false && toProgressState.isStarted == true) return true

    if (isIdeationStageOptions(toProgressState.nextStage?.options)) {
        let isNextStep = fromProgressState.nextStep?.id == toProgressState.currentStep?.id
        let isNextStage = fromProgressState.nextStage?.id == toProgressState.currentStage?.id
        return isNextStep && isNextStage
    } else {
        let isNextStage = fromProgressState.nextStage?.id == toProgressState.currentStage?.id
        return isNextStage
    }
}

// TODO: tests
const isGroupChange = (fromProgressState: IEventProgressState, toProgressState: IEventProgressState) => {
    if (toProgressState == null || toProgressState.currentStage == null) return false

    // if fromProgressState is null, but toProgressState is not null and has a non-null currentGroup, then it is a group change
    if (fromProgressState == null && toProgressState?.currentGroup) return true

    let isGroupChange = fromProgressState?.currentGroup?.id != toProgressState?.currentGroup?.id
    return isGroupChange
}

// TODO: tests
const isStageChange = (fromProgressState: IEventProgressState, toProgressState: IEventProgressState) => {
    if (fromProgressState == null && toProgressState == null) return false

    // if fromProgressState is null, but toProgressState is not null and has a non-null currentStage, then it is a stage change
    if (fromProgressState == null && toProgressState.currentStage) return true

    // if to state is null, then it's an event close
    if (fromProgressState?.currentStage && (toProgressState == null || toProgressState.currentStage == null)) return false

    let isStageChange = fromProgressState?.currentStage?.id != toProgressState?.currentStage?.id
    return isStageChange
}

// TODO: tests
const isStepChange = (fromProgressState: IEventProgressState, toProgressState: IEventProgressState) => {
    if (fromProgressState == null && toProgressState == null) return false

    // if fromProgressState is null, but toProgressState is not null and has a non-null currentStep, then it is a step change
    if (fromProgressState == null && toProgressState.currentStep) return true

    let isStepChange = fromProgressState?.currentStep?.id != toProgressState?.currentStep?.id && toProgressState?.currentStep != null
    return isStepChange
}

// TODO: there's a halted prop in redux
const isHaltChange = (fromProgressState: IEventProgressState, toProgressState: IEventProgressState, closed) => {
    //const isHaltChange = (toProgressState == null || toProgressState.currentStage == null) && !closed;
    //const isHaltChange = (toProgressState.isHalted == true) && !closed;


    // on both to and from states there's a halt flag. if the halt flag changes than it's a halt change.
    if (fromProgressState != null && toProgressState != null) {
        let isHaltChange = fromProgressState.isHalted != toProgressState.isHalted
        return isHaltChange
    }
    return false
}

const isCloseChange = (fromProgressState: IEventProgressState, toProgressState: IEventProgressState, closed) => {
    if (fromProgressState == null) return false
    let isCloseChange = fromProgressState.currentStage
        && (toProgressState == null || toProgressState.currentStage == null) && closed
    return isCloseChange
}

//#enregion

//#region Step functions

const isFirstStep = (template: Template, stage: Stage, step: Step) => {
    if (!template || !stage || !step) return false

    let isStageIdeation = isIdeationStageOptions(stage?.options)
    if (isStageIdeation) {
        let stageOptions = stage?.options as IdeationStageOptions
        let stepIndex = stageOptions.steps.findIndex((s) => s.id == step.id)
        return stepIndex == 0
    } else {
        return false
    }
}

const isLastStep = (template: Template, stage: Stage, step: Step) => {
    if (!template || !stage || !step) return false

    let isStageIdeation = isIdeationStageOptions(stage?.options)
    if (isStageIdeation) {
        let stageOptions = stage?.options as IdeationStageOptions
        let stepIndex = stageOptions.steps.findIndex((s) => s.id == step.id)
        return stepIndex == stageOptions.steps.length - 1
    } else {
        return false
    }
}

export const getParentStage = (template: Template, step: Step) => {
    if (!template || !step) return null

    // find ideation stages
    let ideationStages = template.stages.filter((s) => isIdeationStageOptions(s?.options))
    if (ideationStages.length == 0) return null
    // for each ideation stage loop through the steps and find the step that has the step parameter as a child
    for (let i = 0; i < ideationStages.length; i++) {
        let stage = ideationStages[i]
        let stageOptions = stage?.options as IdeationStageOptions
        for (let j = 0; j < stageOptions.steps.length; j++) {
            let stageStep = stageOptions.steps[j]
            if (stageStep.id == step.id) {
                return stage
            }
        }
    }
    return null
}

export const getStepIndex = (template: Template, stage: Stage, stepId: string) => {
    if (!template || !stage || !stepId) return -1

    const isStageIdeation = isIdeationStageOptions(stage?.options)
    if (isStageIdeation) {
        let stageOptions = stage?.options as IdeationStageOptions
        let stepIndex = stageOptions.steps.findIndex((s) => s.id == stepId)
        return stepIndex
    } else {
        return -1
    }
}

export const getNextStep = (template: Template, currentStage: Stage, currentStep: Step): Step => {
    var nextStep: Step = null

    if (currentStage == null) return null

    if (isIdeationStageOptions(currentStage?.options)) {
        let currentStepIndex = getStepIndex(template, currentStage, currentStep?.id)
        if (currentStepIndex == -1) return null
        if (currentStepIndex + 1 < currentStage?.options.steps.length) {
            nextStep = currentStage?.options.steps[currentStepIndex + 1]
        }
    } else {
        // check if next stage is an ideation
        let nextStage = getNextStage(template, currentStage, currentStep)
        if (nextStage && isIdeationStageOptions(nextStage?.options)) {
            nextStep = nextStage?.options.steps[0]
        }
    }

    return nextStep
}

export const getBackStep = (template: Template, currentStage: Stage, currentStep: Step): Step => {
    let backStep: Step = null

    if (currentStage == null) return null

    if (isIdeationStageOptions(currentStage?.options)) {
        let currentStepIndex = getStepIndex(template, currentStage, currentStep?.id)
        if (currentStepIndex == -1) return null
        if (currentStepIndex - 1 >= 0) {
            backStep = currentStage?.options.steps[currentStepIndex - 1]
        }
    } else {
        // check if previous stage is an ideation
        let previousStage = getBackStage(template, currentStage, currentStep)
        if (previousStage && isIdeationStageOptions(previousStage?.options)) {
            backStep = previousStage?.options.steps[previousStage?.options.steps.length - 1]
        }
    }

    return backStep
}

//#endregion

//#region Stage functions

export const isFirstStage = (template, stageId) => {
    if (!template || !template.stages) return false

    let firstStage = template.stages[0]
    return firstStage && firstStage.id == stageId
}

export const isLastStage = (template, stageId) => {
    if (!template || !template.stages) return false

    let lastStage = template.stages[template.stages.length - 1]
    return lastStage && lastStage.id == stageId
}

export const getStageIndex = (template, stageId) => {
    if (!template || !template.stages) return -1

    let stageIndex = 0
    for (let i = 0; i < template.stages.length; i++) {
        if (template.stages[i].id == stageId) {
            stageIndex = i
        }
    }
    return stageIndex
}

export const getNextStage = (template: Template, currentStage: Stage, currentStep: Step): Stage => {
    let nextStage: Stage = null

    if (currentStage == null || isLastStage(template, currentStage.id)) return null

    let isIdeationStage = isIdeationStageOptions(currentStage?.options)
    let isLastStepOfIdeation = isIdeationStage && isLastStep(template, currentStage, currentStep)

    if (isLastStepOfIdeation || !isIdeationStage) {
        nextStage = template.stages[getStageIndex(template, currentStage.id) + 1] as Stage
    } else {
        nextStage = currentStage
    }

    return nextStage
}

export const getBackStage = (template: Template, currentStage: Stage, currentStep: Step): Stage => {
    let backStage: Stage = null

    if (currentStage == null) return null

    let isIdeationStage = isIdeationStageOptions(currentStage?.options)
    let isFirstStepOfIdeation = isIdeationStage && isFirstStep(template, currentStage, currentStep)

    let currentStageIndex = getStageIndex(template, currentStage.id)
    let previousStageExists = currentStageIndex > 0
    if ((!isIdeationStage || isFirstStepOfIdeation) && previousStageExists) backStage = template.stages[currentStageIndex - 1] as Stage
    if (isIdeationStage && !isFirstStepOfIdeation) backStage = currentStage

    return backStage
}

//#endregion

//#region Group functions

export const isFirstGroup = (template, groupId) => {
    if (!template || !template.groups) return false

    // Group order is not the template.groups order. Instead it is defined by the order of the stages.
    // If any stage has a group property, it is expected that all stages have one.

    // find the first stage
    let firstStage = template.stages[0]
    if (!firstStage) return false
    return firstStage.group == groupId
}

export const isLastGroup = (template, groupId) => {
    if (!template || !template.groups) return false

    // Group order is not the template.groups order. Instead it is defined by the order of the stages.
    // If any stage has a group property, it is expected that all stages have one.

    // find the last stage
    let lastStage = template.stages[template.stages.length - 1]
    return lastStage?.group && lastStage.group == groupId
}

export const getGroupById = (template, groupId) => {
    if (!template || !template.groups) return null

    let group = null
    for (let i = 0; i < template.groups.length; i++) {
        if (template.groups[i].id == groupId) {
            group = template.groups[i]
        }
    }
    return group
}

//#endregion

//#endregion

//#region Voting

export const useIdeaVoteCount = (idea: Idea) => {
    const [ideaUpVotes, setIdeaUpVotes] = useState(0)
    useEffect(() => {
        let upvotes = 0
        idea.stage_step_actions?.map((a) => {
            if (a.stage_step_action) {
                if (a.stage_step_action.dotvote == "up") {
                    upvotes++
                }
            }
        })
        setIdeaUpVotes(upvotes)
    }, [idea.stage_step_actions])

    return ideaUpVotes
}

export const getParticipantIdeaVoteCount = (idea, participantId) => {
    let ideaUpVotes = 0
    idea.stage_step_actions?.map((a) => {
        if (a.stage_step_action) {
            if (a.stage_step_action.dotvote == "up" && a.participant_id == participantId) {
                ideaUpVotes++
            }
        }
    })
    return ideaUpVotes
}

type MaybeCleanUpFn = void | (() => void)

function useCustomCompareMemo<T>(value: T, equal: (a: T, b: T) => boolean): T {
    const ref = useRef<T>(value)

    if (!equal(value, ref.current)) {
        ref.current = value
    }

    return ref.current
}

function useCustomCompareEffect<T>(create: () => MaybeCleanUpFn, input: T, equal: (a: T, b: T) => boolean) {
    useEffect(create, [useCustomCompareMemo(input, equal)])
}

export const useParticipantTotalVoteCount = (ideas: SummedIdea[], participantId: uuidv4, stageId: uuidv4) => {
    const [votes, setVotes] = useState(0)

    useCustomCompareEffect(() => {
        let participantUpVotesCasted = 0
        if (ideas) {
            ideas.map((i) => {
                if (isIdea(i))
                    i.stage_step_actions?.map((a) => {
                        a.participant_id == participantId
                            && a.stage_id == stageId
                            && a.stage_step_action?.dotvote == "up"
                            && participantUpVotesCasted++
                    })
            }
            )
        }
        setVotes(participantUpVotesCasted)
    }, [ideas, participantId, stageId], (a, b) => {
        let same = true
        same = isEqual(a, b)
        return same
    })
    return votes
}

//#endregion

//#region Grouping

export type ITopListItem = SummedIdea & {
    voteCount: number
    selected?: boolean
    color?: string
}

export const getStageIdeaToplist = (ideas: DeepPartial<SummedIdea>[], stage: DeepPartial<Stage>): ITopListItem[] => {
    let ideaToplist: ITopListItem[] = []

    ideas.map((_i: SummedIdea) => {
        let i = { ..._i } as ITopListItem

        let ideaUpVotes = 0

        let stageOptions = stage?.options as StageOptions

        // if idea group
        if (isIdeaGroup(i)) {
            if ((isGroupingStageOptions(stageOptions) || isDotVotingStageOptions(stageOptions)) && i.stage_id == stage?.id) {
                i.ideas = i.ideas.map<ITopListItem>((_subIdea) => {
                    let subIdeaUpVotes = 0
                    let subIdea = { ..._subIdea } as ITopListItem
                    if (isIdea(subIdea)) {
                        if (subIdea.stage_step_actions) {
                            subIdea.stage_step_actions.map((subAction) => {
                                if (subAction.stage_step_action) {
                                    if (subAction.stage_step_action.dotvote == "up") {
                                        ideaUpVotes++
                                        subIdeaUpVotes++
                                    }
                                }
                            })
                        }
                        (subIdea as ITopListItem).voteCount = subIdeaUpVotes
                        return subIdea as ITopListItem
                    }
                }) as Idea[]
            }
        }
        // if idea
        else {
            if ((isGroupingStageOptions(stageOptions) || isDotVotingStageOptions(stageOptions)) && i.stage_id == stageOptions.ideation_stage_id) {
                i.stage_step_actions &&
                    i.stage_step_actions.map((a) => {
                        if (a.stage_step_action) {
                            if (a.stage_step_action.dotvote == "up") {
                                ideaUpVotes++
                            }
                        }
                    })
            }
        }

        if (ideaUpVotes > 0) {
            i.voteCount = ideaUpVotes
            ideaToplist.push(i)
        }
    })

    ideaToplist.sort((a, b) => b.voteCount - a.voteCount)

    //this.setState({ ideaToplist: ideaToplist })
    return ideaToplist
}

//#endregion

//#region  Participants

export const sortParticipants = (participants, participant) => {
    if (participants && participant) {
        let ret = [...participants].sort((a, b) => {
            if (a.id == participant.id) return -1
            if (b.id == participant.id) return 1

            if (a.is_host) return -1
            if (b.is_host) return 1

            if (a.name.toLowerCase() < b.name.toLowerCase()) return -1
            if (a.name.toLowerCase() > b.name.toLowerCase()) return 1
            return 0
        })
        return ret
    }
}

var initialMaxFontSize = null
const increments = 5
export const fitTextIntoContainer = (container, textElement) => {
    let containerComputedStyle = window.getComputedStyle(container)
    let containerHeight = container.clientHeight - (parseFloat(containerComputedStyle.paddingTop) + parseFloat(containerComputedStyle.paddingBottom))

    if (initialMaxFontSize == null) initialMaxFontSize = parseFloat(window.getComputedStyle(textElement).getPropertyValue("font-size"))

    let textElementFontSize = parseFloat(window.getComputedStyle(textElement).getPropertyValue("font-size"))

    if (textElement.offsetHeight > containerHeight || textElementFontSize < initialMaxFontSize) {
        console.log("text fit needed for", textElement)
        let maxFontSize = initialMaxFontSize
        let minFontSize = 0
        let lastFontSize = maxFontSize

        let i = 0
        while (true) {
            let currentFontSize = lastFontSize - (maxFontSize - minFontSize) / 2
            textElement.style.fontSize = currentFontSize + "px"
            console.table({
                increment: i,
                currentFontSize,
                maxFontSize,
                initialMaxFontSize,
                minFontSize,
                lastFontSize,
                containerHeight,
                textHeight: textElement.offsetHeight,
            })
            if (textElement.offsetHeight >= containerHeight) {
                console.log("too big")
                maxFontSize = currentFontSize
                lastFontSize = currentFontSize
                i++
                continue
            }
            if (textElement.offsetHeight < containerHeight) {
                console.log("too small")
                if (i >= increments) break // we only want to iterate at least x increments, but at least up until the font size is smaller
                minFontSize = currentFontSize
                lastFontSize = maxFontSize
                i++
                continue
            }
        }
    }
}

//#endregion

export const getIdeasForStageStep = (ideas: DeepPartial<SummedIdea[]>, stageId: string, stepId: string, stageType: string, template: Template) => {
    let relatedIdeas = []
    if (stageType == "ideation") {
        ideas.map((idea) => {
            if (isIdea(idea) && idea.stage_id == stageId && idea.step_id == stepId) {
                relatedIdeas.push(idea)
            }
        })
    } else if (stageType == "dotvoting") {
        const stageTemplate = template.stages.find((s) => s.id == stageId)
        let ideationStageVotedOn = template.stages.find((s) => {
            return s.id === (stageTemplate?.options as DotVotingStageOptions)?.ideation_stage_id
        })
        let ideationStepsVotedOn = (ideationStageVotedOn?.options as IdeationStageOptions)?.steps.filter((step) => {
            return !step.restrictions || step.restrictions.indexOf("dotvoting") == -1
        })
        ideationStepsVotedOn.map((votingStep) => {
            const ideasVotedOnFromStep = getIdeasForStageStep(ideas, ideationStageVotedOn.id, votingStep.id, ideationStageVotedOn.type, template)
            relatedIdeas = relatedIdeas.concat(ideasVotedOnFromStep)
        })
    }
    relatedIdeas.sort((a, b) => (a.added > b.added ? -1 : 1))

    return relatedIdeas
}

export const getParticipantIdeasForStageStep = (
    ideas: DeepPartial<SummedIdea[]>,
    stageId: string,
    stepId: string,
    stageType: string,
    template: Template,
    participantId: uuidv4
) => {
    const relatedIdeas = getIdeasForStageStep(ideas, stageId, stepId, stageType, template)
    return relatedIdeas.filter(idea => idea.participant_id === participantId)
}

// #region Template Generation

export const generateCustomTemplate = (eventTitle, eventDescription, stageGroupNames) => {
    let stageTemplates = []
    let groupTemplates = []
    stageGroupNames.map(stageGroupName => {
        const groupId = getUuidv4()
        const groupStages = generateStageGroupStages(groupId)
        groupTemplates.push({
            id: groupId,
            name: stageGroupName
        })
        stageTemplates = [...stageTemplates, ...groupStages]

    })
    let customTemplate = {
        id: getUuidv4(),
        name: eventTitle,
        description: eventDescription,
        groups: groupTemplates,
        stages: stageTemplates,
        version: "1.0.0"
    }

    return customTemplate
}

const generateStageGroupStages = (groupId) => {
    const ideationStageId = getUuidv4()
    return [
        {
            id: ideationStageId,
            name: "Ideate",
            type: "ideation",
            group: groupId,
            description: "Share your thoughts on the board. Keep them related to our topic.",
            options: {
                steps: [
                    {
                        id: getUuidv4(),
                        name: "Ideate",
                        duration: 5,
                    },
                ],
            },
            instructions: [
                "Every idea or thought is important.",
                "Feel free to share anything that comes to mind and add to what others have said.",
                "Let’s keep an open mind and think about all the different possibilities we can explore, without judging any idea."
            ],
        },
        {
            id: getUuidv4(),
            name: "Vote",
            type: "dotvoting",
            group: groupId,
            description: "Vote on the ideas you think are most important to discuss and act on.",
            options: {
                rules: { vote_per_participant: 5 },
                ideation_stage_id: ideationStageId,
            },
            instructions: [
                "Use the plus and minus buttons to vote.",
                "You have a limited number of votes, so use them wisely.",
                "You can vote on as many ideas as you like, and you can vote more than once on each idea.",
                "You can change your votes at any time, so feel free to move them around as you see fit.",
                "The ideas with the most votes will be the ones we talk about and act on first.",
                "Consider not only your own perspective but also the broader impact."
            ],
        },
        {
            id: getUuidv4(),
            name: "Combine",
            type: "grouping",
            group: groupId,
            description: "The Host will group similar thoughts together to see which ideas are related and how important they are as a whole.",
            options: { ideation_stage_id: ideationStageId },
            instructions: [
                "The Host will group similar ideas together.",
                "The Host will ask for your input on how the ideas are grouped.",
                "Make sure to talk with the Host about how the ideas are being grouped.",
                "This will help us decide what to focus on next and what our priorities should be."
            ],
        },
    ]

}

// #endregion

// #region colors

export const themeColorNames = ['violet', 'purple', 'fuchsia', 'pink', 'rose', 'orange', 'amber', 'yellow', 'lime', 'green', 'emerald', 'teal', 'cyan', 'sky', 'blue', 'indigo']

// #endregion